/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

export {
	isIterable,
	isPrimitive,
	isSymbol,
	isBoolean,
	isString,
	isObject,
	isInstance,
	isArray,
	isFunction,
	isInteger,
};

/**
 * With this function you can check if a value is iterable.
 *
 * This method is used in the library to have consistent names.
 *
 * You can call the method via the monster namespace `Monster.Types.isPrimitive()`.
 *
 * @externalExample ../../example/types/is-1.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.2.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isIterable(value) {
	if (value === undefined) return false;
	if (value === null) return false;
	return typeof value?.[Symbol.iterator] === "function";
}

/**
 * Checks whether the value passed is a primitive (string, number, boolean, NaN, undefined, null or symbol)
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-2.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isPrimitive(value) {
	var type;

	if (value === undefined || value === null) {
		return true;
	}

	type = typeof value;

	if (
		type === "string" ||
		type === "number" ||
		type === "boolean" ||
		type === "symbol"
	) {
		return true;
	}

	return false;
}

/**
 * Checks whether the value passed is a symbol
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-3.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.9.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isSymbol(value) {
	return "symbol" === typeof value ? true : false;
}

/**
 * Checks whether the value passed is a boolean.
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-4.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isBoolean(value) {
	if (value === true || value === false) {
		return true;
	}

	return false;
}

/**
 * Checks whether the value passed is a string
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-5.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isString(value) {
	if (value === undefined || typeof value !== "string") {
		return false;
	}
	return true;
}

/**
 * Checks whether the value passed is a object
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-6.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isObject(value) {
	if (isArray(value)) return false;
	if (isPrimitive(value)) return false;

	if (typeof value === "object") {
		return true;
	}

	return false;
}

/**
 * Checks whether the value passed is a object and instance of instance.
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-7.mjs
 * @param {*} value
 * @param {*} instance
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.5.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isInstance(value, instance) {
	if (!isObject(value)) return false;
	if (!isFunction(instance)) return false;
	if (!instance.hasOwnProperty("prototype")) return false;
	return value instanceof instance ? true : false;
}

/**
 * Checks whether the value passed is a array
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-8.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 * @see https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Array/isArray
 */
function isArray(value) {
	return Array.isArray(value);
}

/**
 * Checks whether the value passed is a function
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-9.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isFunction(value) {
	if (isArray(value)) return false;
	if (isPrimitive(value)) return false;

	if (typeof value === "function") {
		return true;
	}

	return false;
}

/**
 * Checks whether the value passed is an integer.
 *
 * This method is used in the library to have consistent names.
 *
 * @externalExample ../../example/types/is-10.mjs
 * @param {*} value
 * @returns {boolean}
 * @license AGPLv3
 * @since 1.4.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
function isInteger(value) {
	return Number.isInteger(value);
}
